import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Calendar, dateFnsLocalizer, Navigate } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import addDays from 'date-fns/addDays';
import subDays from 'date-fns/subDays';
import zhTW from 'date-fns/locale/zh-TW';


// 引入 API
import { getManagerShiftRequests } from '../../services/api';

// 1. 設定 Localizer
const locales = { 'zh-TW': zhTW };
const localizer = dateFnsLocalizer({
  format, parse, startOfWeek, getDay, locales,
});

// 2. 設定時間範圍 (跟 RosterCalendar 一致)
const minTime = new Date();
minTime.setHours(10, 0, 0);
const maxTime = new Date();
maxTime.setHours(23, 30, 0);

// ==========================================
// 🎨 自訂 Event 顯示組件
// ==========================================
const CustomEvent = ({ event }) => {
  return (
    <div className="h-full flex flex-col justify-center leading-none px-1">
      {/* 上面：時間 */}
      <div className="text-[10px] font-bold opacity-90 mb-0.5">
        {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
      </div>
      
      {/* 下面：人名 + 備註 */}
      <div className="text-xs font-normal leading-tight whitespace-normal break-words">
        {/* 顯示：[李員工] 早更 */}
        <span className="font-bold text-yellow-200 mr-1">{event.staffName}</span>
        <span>{event.originalTitle}</span>
      </div>
    </div>
  );
};

// ==========================================
// 🛠️ 自訂 Toolbar (跟 RosterCalendar 一樣，但移除 Save/Select)
// ==========================================
const IntegratedToolbar = ({ onNavigate, label, view, onView, onRefresh }) => {
    const goToBack = () => onNavigate(Navigate.PREVIOUS);
    const goToNext = () => onNavigate(Navigate.NEXT);
    const goToToday = () => onNavigate(Navigate.TODAY);

    const handleViewChange = (e) => {
        onView(e.target.value);
        onNavigate(Navigate.TODAY);
    };

    return (
      <div className="flex items-center justify-between p-1 mb-1 bg-gray-50 border-b border-gray-200 gap-2">
        
        {/* 左邊：導航區 */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* View 切換 */}
          <div className="relative">
              <select
                  value={view}
                  onChange={handleViewChange}
                  className="appearance-none pl-2 pr-6 py-3 bg-purple-50 border border-purple-200 rounded-md text-xs font-bold text-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer shadow-sm"
              >
                  <option value="month">📅 月</option>
                  <option value="week">📅 週</option>
                  <option value="day">📅 日</option>
              </select>
          </div>

          {/* 日期導航 */}
          <div className="flex items-center bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden">
             <button onClick={goToBack} className="p-3 px-4 text-gray-500 hover:text-purple-600 hover:bg-gray-50 border-r border-gray-200 transition-colors">
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
             </button>
             <span className="text-sm font-bold text-gray-800 px-6 whitespace-nowrap text-center min-w-[2.5rem]">
                {label.replace('2025年', '')}
             </span>
             <button onClick={goToNext} className="p-3 px-4 text-gray-500 hover:text-purple-600 hover:bg-gray-50 border-l border-gray-200 transition-colors">
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
             </button>
          </div>
        </div>

        {/* 右邊：Refresh 按鈕 (取代 Save) */}
        <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
           <span className="text-xs text-gray-400 hidden sm:block mr-2">
              (唯讀模式: 顯示員工意願)
           </span>
           <button 
              onClick={onRefresh}
              className="p-3 bg-white border border-gray-300 text-gray-700 rounded shadow-sm hover:bg-gray-50 active:bg-gray-100 flex-shrink-0"
              title="重新整理"
           >
              <span className="text-sm">🔄</span>
           </button>
        </div>
      </div>
    );
};

// ==========================================
// 🌟 主組件
// ==========================================
const ManagerRequestView = () => {
  const [events, setEvents] = useState([]);
  
  // 跟 RosterCalendar 一樣控制 Date 和 View
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('week');

  // 1. 載入資料
  const fetchRequests = useCallback(async () => {
    try {
      // 這裡建議加上 start/end filter 以優化效能，暫時拿全部
      const response = await getManagerShiftRequests();
      const dataArray = Array.isArray(response.data) ? response.data : response.data.results;

      const loadedEvents = dataArray.map(evt => ({
          ...evt,
          id: evt.id,
          title: `${evt.staff_name} - ${evt.title}`, 
          originalTitle: evt.title, // 保留原始標題
          start: new Date(evt.start),
          end: new Date(evt.end),
          resourceId: evt.resourceId,
          staffName: evt.staff_name // 存起人名
      }));
      /*
      const loadedEvents = response.data.results.map(evt => ({
        id: evt.id,
        // 組合 Title 供 Default View 用，但 CustomEvent 會分開顯示
        title: `${evt.staff_name} - ${evt.title}`, 
        originalTitle: evt.title, // 保留原始標題
        start: new Date(evt.start),
        end: new Date(evt.end),
        resourceId: evt.resourceId,
        staffName: evt.staff_name // 存起人名
      }));
      */
      setEvents(loadedEvents);
    } catch (err) {
      console.error("Load requests failed:", err);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // 2. 自訂格式 (跟 RosterCalendar 完全一樣)
  const formats = useMemo(() => ({
      monthHeaderFormat: (date, culture, localizer) => localizer.format(date, 'yyyy年 MM月', culture),
      dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
        `${localizer.format(start, 'MM/dd', culture)} - ${localizer.format(end, 'MM/dd', culture)}`,
      dayFormat: (date, culture, localizer) => localizer.format(date, 'MM/dd (eeeee)', culture),
      eventTimeRangeFormat: () => "", // 隱藏預設時間，用 CustomEvent 顯示
      timeGutterFormat: (date, culture, localizer) => localizer.format(date, 'HH:mm', culture),
      dateFormat: (date, culture, localizer) => localizer.format(date, 'd', culture),
  }), []);

  // 3. Event 樣式 (用紫色區分)
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: '#9333ea', // Purple-600 (區分於正式更表的藍色/綠色)
        borderRadius: '6px',
        opacity: 0.9,
        border: '1px solid rgba(255,255,255,0.2)',
        color: 'white',
        display: 'block',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
      }
    };
  };

  // 4. 處理導航
  const onNavigate = useCallback((newDate) => setDate(newDate), []);
  const onView = useCallback((newView) => setView(newView), []);

  return (
<div className="flex flex-col h-full"> 
      
      <div className="flex-grow relative" style={{ minHeight: '0', touchAction: 'none'}}>
        <Calendar
          className="locked-roster-view"
          date={date}
          view={view}
          onNavigate={onNavigate}
          onView={onView}
          
          dayLayoutAlgorithm='no-overlap' 
          culture='zh-TW'
          
          // 注入自訂組件
          components={{
            toolbar: (props) => <IntegratedToolbar {...props} onRefresh={fetchRequests} />,
            event: CustomEvent
          }}
          
          localizer={localizer}
          formats={formats}
          events={events}
          
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          
          eventPropGetter={eventStyleGetter}
          
          // 設定為唯讀
          selectable={false} 
          
          step={30}
          timeslots={2}
          min={minTime}
          max={maxTime}
        />
      </div>
    </div>
  );
};

export default ManagerRequestView;